// File: app/src/components/session/ImportFitDialog.js
//
// Import activities from Garmin .fit files (#77).
//
// Drop in one activity or the whole bulk export — every file is decoded, every
// activity inside is listed, and nothing is written until the user picks. That
// review step is the point: a bulk export can hold years, and an import that
// silently wrote hundreds of rows would be far harder to undo than to confirm.
//
// Activities already in history are detected by their start instant and
// pre-unticked rather than hidden, so re-importing an overlapping export shows
// you what it skipped instead of appearing to lose files.

import React, { useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { AlertCircle, CheckCircle2, Loader2, Upload } from "lucide-react";
import {
  buildImportCandidates,
  dedupeAgainstHistory,
  isFitPath,
  isZipPath,
} from "../../utils/fitImport";

const formatWhen = (date) =>
  date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const ImportFitDialog = ({ open, onOpenChange, existingSessions, onImport }) => {
  const inputRef = useRef(null);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [reading, setReading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [fileErrors, setFileErrors] = useState([]);
  const [imported, setImported] = useState(0);
  // Progress for the two long phases. A bulk export can hold thousands of
  // files, and a dialog that simply sits there reads as a hang.
  const [progress, setProgress] = useState(null);

  const reset = () => {
    setCandidates([]);
    setSelected(new Set());
    setError(null);
    setFileErrors([]);
    setImported(0);
    setProgress(null);
  };

  const close = (next) => {
    onOpenChange(next);
    if (!next) reset();
  };

  const readFiles = async (fileList) => {
    const chosenFiles = Array.from(fileList || []).filter(
      (f) => isFitPath(f.name) || isZipPath(f.name),
    );
    if (chosenFiles.length === 0) {
      setError(
        "Choose .fit files, or the .zip Garmin sends when you request your data export.",
      );
      return;
    }

    setReading(true);
    setError(null);
    setFileErrors([]);
    setImported(0);
    setProgress(null);

    const failures = [];

    // Unpack any archives first, so the decode phase sees one flat list and can
    // report honest progress against it.
    const fitFiles = [];
    let truncated = false;
    for (const file of chosenFiles) {
      if (isFitPath(file.name)) {
        fitFiles.push({ name: file.name, buffer: await file.arrayBuffer() });
        continue;
      }
      try {
        setProgress({ phase: "unzip", done: 0, total: null });
        const { extractFitFiles } = await import("../../apis/fitArchive");
        const result = await extractFitFiles(file, {
          onProgress: (done) =>
            setProgress({ phase: "unzip", done, total: null }),
        });
        fitFiles.push(...result.files);
        truncated = truncated || result.truncated;
      } catch (err) {
        failures.push(`${file.name}: ${err.message || "could not be opened"}`);
      }
    }

    if (truncated) {
      failures.push(
        "That export held more activities than one import can take. The oldest were left out — import the rest as a second pass.",
      );
    }

    // Dynamic import for the same reason llmApi uses one: the FIT SDK carries a
    // large generated profile table, and nobody should download it to look at
    // their history.
    const { decodeFitFile } = await import("../../apis/fitDecoder");

    const found = [];
    for (let i = 0; i < fitFiles.length; i++) {
      const file = fitFiles[i];
      setProgress({ phase: "decode", done: i + 1, total: fitFiles.length });
      try {
        const { messages, exerciseNames } = await decodeFitFile(file.buffer);
        found.push(...buildImportCandidates(messages, exerciseNames));
      } catch (err) {
        failures.push(`${file.name}: ${err.message || "could not be read"}`);
      }
      // Yield to the event loop every so often: decoding hundreds of files back
      // to back would otherwise block paint and freeze the progress it is
      // reporting.
      if (i % 25 === 24) await new Promise((r) => setTimeout(r, 0));
    }

    // One activity can appear in two exported files; keep the first of each.
    const unique = [];
    const seen = new Set();
    for (const candidate of found) {
      const key = candidate.summary.externalId;
      if (key && seen.has(key)) continue;
      if (key) seen.add(key);
      unique.push(candidate);
    }

    const flagged = dedupeAgainstHistory(unique, existingSessions);
    setCandidates(flagged);
    setSelected(
      new Set(
        flagged
          .filter((c) => !c.alreadyImported)
          .map((c) => c.summary.externalId),
      ),
    );
    setFileErrors(failures);
    if (flagged.length === 0 && failures.length === 0) {
      setError("No activities found in those files.");
    }
    setProgress(null);
    setReading(false);
  };

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const chosen = useMemo(
    () => candidates.filter((c) => selected.has(c.summary.externalId)),
    [candidates, selected],
  );

  const runImport = async () => {
    setImporting(true);
    setError(null);
    let count = 0;
    const failures = [];
    // Sequential rather than parallel: a bulk export can hold hundreds, and
    // firing them all at once would flood the API for no gain on a task nobody
    // is watching the clock on.
    for (const candidate of chosen) {
      const failure = await onImport(candidate.payload);
      if (failure) failures.push(`${candidate.summary.label}: ${failure}`);
      else count += 1;
      setImported(count);
    }
    setImporting(false);
    setImported(count);
    setFileErrors(failures);
    if (failures.length === 0) close(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg max-h-[90dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Import from Garmin</DialogTitle>
          <DialogDescription>
            Drop in .fit files from Garmin Connect, or the .zip from a full
            account export — nested archives and all. Strength sessions bring
            their sets, reps and weights with them.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
          <input
            ref={inputRef}
            type="file"
            accept=".fit,.zip"
            multiple
            className="hidden"
            onChange={(e) => readFiles(e.target.files)}
          />

          <Button
            variant="outline"
            className="w-full gap-1.5"
            onClick={() => inputRef.current?.click()}
            disabled={reading || importing}
          >
            {reading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {!reading
              ? "Choose files"
              : progress?.phase === "unzip"
                ? `Unpacking… ${progress.done} found`
                : progress?.phase === "decode"
                  ? `Reading ${progress.done} of ${progress.total}…`
                  : "Reading files…"}
          </Button>

          {reading && progress?.phase === "decode" && progress.total > 0 && (
            <div
              className="h-1 w-full overflow-hidden rounded-full bg-muted"
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={progress.total}
              aria-valuenow={progress.done}
            >
              <div
                className="h-full bg-primary transition-[width] duration-200"
                style={{
                  width: `${Math.round((progress.done / progress.total) * 100)}%`,
                }}
              />
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {fileErrors.length > 0 && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 space-y-1">
              {fileErrors.map((message) => (
                <p key={message} className="text-xs text-destructive">
                  {message}
                </p>
              ))}
            </div>
          )}

          {imported > 0 && fileErrors.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {`${imported} imported before the errors above.`}
            </p>
          )}

          {candidates.length > 0 && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between px-1">
                <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                  {`${candidates.length} found`}
                </span>
                <button
                  type="button"
                  className="text-[11px] text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  onClick={() =>
                    setSelected((prev) =>
                      prev.size === candidates.length
                        ? new Set()
                        : new Set(candidates.map((c) => c.summary.externalId)),
                    )
                  }
                >
                  {selected.size === candidates.length
                    ? "Select none"
                    : "Select all"}
                </button>
              </div>

              {candidates.map(({ summary, alreadyImported }) => (
                <label
                  key={summary.externalId}
                  className="flex items-center gap-3 rounded-lg bg-muted/40 px-3 py-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(summary.externalId)}
                    onChange={() => toggle(summary.externalId)}
                    className="h-4 w-4 shrink-0 accent-primary"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {summary.label}
                    </p>
                    <p className="text-[11px] text-muted-foreground">
                      {[
                        formatWhen(summary.startedAt),
                        summary.durationMinutes
                          ? `${summary.durationMinutes} min`
                          : null,
                        summary.exerciseCount > 0
                          ? `${summary.exerciseCount} exercise${summary.exerciseCount === 1 ? "" : "s"}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  </div>
                  {alreadyImported && (
                    <span className="shrink-0 flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                      <CheckCircle2 className="h-3 w-3" />
                      In history
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={() => close(false)} disabled={importing}>
            Cancel
          </Button>
          <Button
            onClick={runImport}
            disabled={importing || chosen.length === 0}
          >
            {importing && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />}
            {importing
              ? `Importing ${imported + 1} of ${chosen.length}…`
              : chosen.length > 0
                ? `Import ${chosen.length}`
                : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportFitDialog;
